import { logError } from '@databases/logs';
import { type DefaultRootState } from '@databases/redux';
import { getService } from '@databases/service-container';
import { ContentRepresentation } from '@databases/types/confluence';
import { DatabaseFieldType, RenderContext } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { convertContentBody } from '@databases/utils/confluence/content';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { parseConfluenceHTML } from '@databases/utils/lib/dom';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { encodeHtml } from '@databases/utils/utils';

import { renderDatabaseValue, renderTableLayoutWithoutStyles } from './render';

export const DATABASES_CELL_CLIPBOARD_ATTR = 'data-databases-cell-clipboard';

/*
 * Extracts plain text from HTML on single click cell copy
 */
export function extractPlainTextFromHtml(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Copies a value to the clipboard
 */
export const copyToClipboard = async (
  value: string,
  type: 'text/plain' | 'text/html'
): Promise<boolean> => {
  try {
    const text = type === 'text/html' ? extractPlainTextFromHtml(value) : value;
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([value], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
    return true;
  } catch (possibleError) {
    const error =
      possibleError instanceof Error ? possibleError : new Error(String(possibleError));
    logError('Error while copying to clipboard', error);
    return false;
  }
};

export const copyHtmlTableToClipboard = (
  contentId: string,
  state: DefaultRootState,
  viewSettings?: DatabaseViewSettings
) => {
  const selectDatabase = selectDatabaseByContentIdRequired;
  const selectionsReader = getService('SelectionsReader');
  const selectedFieldIds = selectionsReader.getFieldIdsFromSelection();
  const selectedEntryIds = selectionsReader.getEntryIdsFromSelection();

  const isSingleCell = selectedFieldIds.length === 1 && selectedEntryIds.length === 1;
  const database = selectDatabase(state, { contentId });
  const isSingleTextCell =
    isSingleCell &&
    getDatabaseFieldRequired(database, selectedFieldIds[0]).type ===
      DatabaseFieldType.TEXT;

  if (isSingleTextCell) {
    const cellHtml = renderDatabaseValue(
      {
        contentId,
        fieldId: selectedFieldIds[0],
        entryId: selectedEntryIds[0],
        renderContext: RenderContext.COPY_DATABASE_TO_CLIPBOARD,
        selectDatabase,
      },
      state
    );
    const markerHtml = `<div ${DATABASES_CELL_CLIPBOARD_ATTR}="true">${cellHtml}</div>`;
    copyToClipboard(markerHtml, 'text/html');
  } else {
    const storageOrHtmlFormat = renderTableLayoutWithoutStyles(
      {
        contentId,
        renderContext: RenderContext.COPY_DATABASE_TO_CLIPBOARD,
        viewSettings,
        selectDatabase,
        selectedEntryIds,
        selectedFieldIds,
      },
      state
    );
    copyToClipboard(storageOrHtmlFormat, 'text/html');
  }
};

/**
 * Removes the <meta> tags, converts <br> to self-closing <br />, and makes
 * img tags self-closing. This is to prevent errors when calling the Confluence
 * endpoint for converting storage format to HTML.
 * @param storageFormat The confluence storage format to sanitize
 */
function sanitizeStorageFormat(storageFormat: string) {
  const scrubbedHtml = storageFormat
    .replace(/<meta[^>]*>/g, '')
    .replace(/<br\s*\/?>/gi, '<br />');
  const imgTagRegex = /<img\b(?![^>]*\/>)([^>]*)>/gi;
  return scrubbedHtml.replace(imgTagRegex, '<img$1 />');
}

/**
 * Converts from storage format to HTML by calling a Confluence REST API
 * endpoint to fetch users and page links, and includes stylesheets.
 * @param storageFormat HTML in storage format
 * @param title
 * @param state Redux state
 * @param contentOnly if true, returns html without styling applied
 */
export async function convertStorageFormatToHtml(
  storageFormat: string,
  title: string,
  state: DefaultRootState,
  contentOnly = false
): Promise<string> {
  const sanitizedStorageFormat = sanitizeStorageFormat(storageFormat);
  const body = await convertContentBody(
    {
      representation: ContentRepresentation.STORAGE,
      value: sanitizedStorageFormat,
    },
    { to: ContentRepresentation.EXPORT_VIEW },
    ['webresource.uris.css', 'webresource.superbatch.uris.css']
  );

  // The result is not valid HTML, as it uses self-closing tags such as <div/> (but it is also not valid XHTML).
  // We need to convert it to valid HTML so that the browsers will interpret the result correctly.
  const html = parseConfluenceHTML(body.value).html()!;

  if (contentOnly) {
    return html;
  } else {
    return (
      `<!DOCTYPE html>` +
      `<html><head><meta charset="UTF-8"><title>${encodeHtml(title)}</title>` +
      `<base href=${encodeHtml(selectBaseUrl(state))}>` +
      `<style>body { background: #fff !important; padding: 15px !important; }</style>` +
      [
        ...(body.webresource?.uris?.css ?? []),
        ...(body.webresource?.superbatch?.uris?.css ?? []),
      ]
        .map((uri) => `<link rel="stylesheet" href="${encodeHtml(uri)}">`)
        .join('') +
      `</head><body>` +
      `<div class="wiki-content">${html}</div>` +
      `</body></html>`
    );
  }
}
